import { CheckIcon } from "@heroicons/react/20/solid";
import { useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast, Bounce } from "react-toastify";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import useIsBigDesktop from "../utils/useIsBigScreen";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import { fontMap } from "../utils/common";

const RemoteParticipantView = ({ }) => {
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isBigDesktop = useIsBigDesktop();
  const micRef = useRef(null);
  const videoRef = useRef(null);

  const {
    selectedLang,
    faceVerified,
    agreedData,
    faceDetectError,
    currentPageData,
    isAbortSignal,
  } = useAppContext();

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLang?.lang);
  }, [selectedLang]);

  const { participants } = useMeeting({});
  const nonLocalParticipantId = Array.from(participants.values()).find(
    (p) => !p.local
  )?.id;

  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    displayName,
    captureImage,
  } = useParticipant(nonLocalParticipantId);

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error)
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute('playsinline', '');
          videoRef.current.setAttribute('x5-playsinline', '');
          videoRef.current.setAttribute('webkit-playsinline', '');
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error)
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  const fontFamilyCSS = fontMap[selectedLang?.lang] || "Lato";

  useEffect(() => {
    if (faceDetectError !== null) {
      let message =
        faceDetectError?.err === "NO_FACE_DETECTED"
          ? "No face detected. Please ensure your face is clearly visible."
          : faceDetectError?.err === "FACE_MATCH_UNSUCESSFUL"
            ? "Face Match Unsucessful!"
            : faceDetectError?.err === "MULTIPLE_FACES_DETECTED"
              ? "Multiple faces detected. Please make sure only one person is in view!"
              : faceDetectError?.err === "SPOOF_DETECTED"
                ? "Make sure a real person is in view and looking towards camera in a well-lit area!"
                : "";

      if (!isAbortSignal) {
        new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
        toast.error(`${t(message)}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
          style: { fontFamily: fontFamilyCSS },
        });
      }
    }
  }, [faceDetectError, isAbortSignal]);

  return webcamOn ? (
    <div
      className={`flex md:flex-1 w-full flex-col items-center relative justify-center`}
    >
      {!faceVerified?.isVerified && (
        <>
          <div
            style={{
              height:
                !isMobile && !isTab && currentPageData !== "VideoConsent"
                  ? "550px"
                  : isTab
                    ? "290px"
                    : isBigDesktop
                      ? "600px"
                      : isMobile
                        ? "250px"
                        : "380px",
              width:
                isMobile || isTab
                  ? "100%"
                  : currentPageData === "VideoConsent"
                    ? isBigDesktop
                      ? "50%"
                      : "70%"
                    : "100%",
            }}
            className={`absolute  rounded-xl bg-[#16161680] z-30`}
          >
            {" "}
          </div>

          <div className="absolute z-40 flex flex-col items-center justify-center h-44 p-0">
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="text-white text-xl text-center mt-0 z-50"
            >
              {`${t("Please hold on while we verify your video")}...`}
            </p>
          </div>
        </>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={true}
        width={
          isMobile || isTab
            ? "100%"
            : currentPageData === "VideoConsent"
              ? isBigDesktop
                ? "50%"
                : "70%"
              : "100%"
        }
        className={
          faceVerified?.isVerified
            ? agreedData
              ? "border-2 border-green-600"
              : "border-2 border-red-700"
            : `relative ${currentPageData !== "VideoConsent" && "w-hull h-full"
            } `
        }
        // className="border border-green-150"
        style={{
          objectFit: "contain",
          borderRadius: 16,
          height:
            !isMobile && !isTab && currentPageData !== "VideoConsent"
              ? "550px"
              : isTab
                ? "290px"
                : isBigDesktop
                  ? "600px"
                  : isMobile
                    ? "250px"
                    : "380px",
          zIndex: 10,
        }}
      />
      <audio ref={micRef} autoPlay playsInline muted={false} />

      {faceVerified?.isVerified &&
        agreedData &&
        currentPageData === "VideoConsent" && (
          <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[370px] z-10 bg-white p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="ml-2 text-green-250 text-sm md:text-base mb-0"
            >
              {t("Face detected")}
            </p>
          </div>
        )}

      {faceVerified?.isVerified && agreedData && isMobile && (
        <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[520px] z-10 bg-white p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
          <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
          <p
            style={{ fontFamily: fontFamilyCSS }}
            className="ml-2 text-green-250 text-sm md:text-base mb-0"
          >
            {t("Face detected")}
          </p>
        </div>
      )}

      {faceVerified?.isVerified &&
        agreedData &&
        currentPageData !== "VideoConsent" &&
        !isMobile && (
          <div className="bg-[#3BA55D1A] p-1.5 md:p-2 mt-2 md:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-green-250" />
            <p
              style={{ fontFamily: fontFamilyCSS }}
              className="ml-2 text-green-250 text-sm md:text-base mb-0"
            >
              {t("Face detected")}
            </p>
          </div>
        )}
    </div>
  ) : null;
};

export default RemoteParticipantView;
